import {HttpBackend, HttpClient, HttpContext, HttpErrorResponse, HttpEventType, HttpResponse,} from '@angular/common/http';
import {inject, Injectable, signal} from '@angular/core';
import {firstValueFrom, Observable, Subject, takeUntil, tap, filter, timer, timeout, TimeoutError} from 'rxjs';

import {environment} from "../../environments/environment";
import {SKIP_AUTH} from "../_utils/skip-auth.token";
import {HANDLE_UPLOAD_ERRORS} from "../_utils/upload-errors.token";

export interface PostUploadHandle {
    done: Promise<{upload_id: string}>;
    progress: () => number;
    abort: () => Promise<void>;
}

export interface InitiateResponse {
    upload_id: string;
    part_size: number;
    part_count: number;
    expires_in: number;
    part_urls: Record<string, string>;
    max_part_urls_per_request?: number;
}

export interface MediaMetadata {
    title?: string;
    description?: string;
    hidden?: boolean;
    cover_image?: boolean;
    note_content?: string;
    added_at?: string;
    cropped?: boolean;
    uuid?: string;
    upload_session_id?: string;
}

export interface DraftItemMetadata {
    title?: string;
    description?: string;
}

interface DirectUploadTarget {
    initiateUrl: string;
    initiateBody: object;
    completeExtra: object;
    completeMessage: string;
}

interface CompletedPart {
    PartNumber: number;
    ETag: string;
}

export class DirectUploadError extends Error {
    constructor(message: string, readonly originalError?: unknown) {
        super(message);
        this.name = 'DirectUploadError';
    }
}

const CONCURRENCY = 4;
const MAX_PART_ATTEMPTS = 3;
const UPLOAD_IDLE_TIMEOUT_MS = 120_000;
const UPLOAD_API_TIMEOUT_MS = 300_000;

@Injectable({providedIn: 'root'})
export class DirectUploadService {
    /** 0..1 aggregate progress across all parts. */
    readonly progress = signal(0);
    /** Last failed upload error, useful for templates or diagnostics. */
    readonly error = signal<DirectUploadError | null>(null);
    /** Human-readable step log for the PoC UI. */
    readonly log = signal<string[]>([]);
    private readonly http = inject(HttpClient);

    // Bucket requests bypass all API authentication and logout interceptors.
    private readonly bucketHttp = new HttpClient(inject(HttpBackend));

    uploadPost(file: File, community: number, target: 'post' | 'comment' = 'post'): PostUploadHandle {
        const base = `${environment.apiUrl}/posts/direct-uploads`;
        const apiOptions = {context: new HttpContext().set(HANDLE_UPLOAD_ERRORS, true)};
        const progress = signal(0);
        const cancelled = new Subject<void>();
        let stopped = false;
        let uploadId: string | undefined;
        let cleanup: Promise<void> | undefined;
        const abort = async (): Promise<void> => {
            stopped = true;
            cancelled.next();
            if (uploadId) {
                cleanup ??= firstValueFrom(this.http.post(`${base}/${uploadId}/abort/`, {}, apiOptions).pipe(timeout(30_000)))
                    .then(() => undefined).catch(() => undefined);
                await cleanup;
            }
        };
        const check = (): void => {
            if (stopped) throw new DirectUploadError('Upload cancelled');
        };
        const waitForRetry = async (attempt: number): Promise<void> => {
            check();
            await firstValueFrom(timer(1000 * 2 ** (attempt - 1)).pipe(takeUntil(cancelled)));
            check();
        };
        // URL refresh and completion are idempotent. Retrying initiation could
        // create a second upload whose id was lost with the first response.
        const retryRequest = async <T>(request: () => Observable<T>): Promise<T> => {
            for (let attempt = 1; ; attempt++) {
                check();
                try {
                    return await firstValueFrom(request().pipe(timeout(UPLOAD_API_TIMEOUT_MS), takeUntil(cancelled)));
                } catch (err) {
                    check();
                    if (attempt >= MAX_PART_ATTEMPTS || !this.isTransientUploadError(err)) throw err;
                    await waitForRetry(attempt);
                }
            }
        };
        const done = (async () => {
            const init = await firstValueFrom(this.http.post<InitiateResponse>(`${base}/initiate/`, {
                community, target, filename: file.name,
                content_type: this.getUploadContentType(file), size: file.size,
            }, apiOptions).pipe(timeout(UPLOAD_API_TIMEOUT_MS)));
            uploadId = init.upload_id;
            check();
            const urls = new Map<number, {url: string; expiresAt: number}>();
            const storeUrls = (partUrls: Record<string, string>, expiresIn: number = 3600): void => {
                const expiresAt = Date.now() + expiresIn * 1000;
                Object.entries(partUrls).forEach(([n, url]) => urls.set(Number(n), {url, expiresAt}));
            };
            storeUrls(init.part_urls, init.expires_in);
            const batchSize = Math.max(1, Math.floor(init.max_part_urls_per_request ?? 100));
            const bytes = new Array<number>(init.part_count).fill(0);
            let loaded = 0;
            const updateProgress = (part: number, partBytes: number): void => {
                loaded += partBytes - bytes[part - 1];
                bytes[part - 1] = partBytes;
                progress.set(Math.min(1, loaded / file.size));
            };
            const parts: CompletedPart[] = [];
            let next = 1;
            let fetching: Promise<void> | undefined;
            const getUrl = async (part: number): Promise<string> => {
                if (fetching) await fetching;
                check();
                const cached = urls.get(part);
                if (!cached || cached.expiresAt <= Date.now() + 30_000) {
                    const part_numbers = Array.from({length: Math.min(batchSize, init.part_count - part + 1)}, (_, i) => part + i);
                    fetching = retryRequest(() => this.http.post<{part_urls: Record<string, string>; expires_in: number}>(
                        `${base}/${uploadId}/part-urls/`, {part_numbers}, apiOptions,
                    )).then(res => {
                        storeUrls(res.part_urls, res.expires_in);
                    });
                    try { await fetching; } finally { fetching = undefined; }
                }
                check();
                const url = urls.get(part);
                if (!url) throw new DirectUploadError('Missing upload URL');
                return url.url;
            };
            const worker = async (): Promise<void> => {
                while (next <= init.part_count) {
                    check();
                    const part = next++;
                    const blob = file.slice((part - 1) * init.part_size, Math.min(part * init.part_size, file.size));
                    for (let attempt = 1; ; attempt++) {
                        try {
                            const url = await getUrl(part);
                            check();
                            const response = await firstValueFrom(this.bucketHttp.put<unknown>(url, blob, {
                                reportProgress: true, observe: 'events',
                            }).pipe(timeout({each: UPLOAD_IDLE_TIMEOUT_MS}), takeUntil(cancelled), tap(event => {
                                if (event.type === HttpEventType.UploadProgress) {
                                    updateProgress(part, Math.min(blob.size, event.loaded));
                                }
                            }), filter((event): event is HttpResponse<unknown> => event instanceof HttpResponse)));
                            const ETag = response.headers.get('ETag');
                            if (!ETag) throw new DirectUploadError('No ETag on upload response; check bucket CORS configuration');
                            updateProgress(part, blob.size);
                            parts.push({PartNumber: part, ETag});
                            break;
                        } catch (err) {
                            check();
                            updateProgress(part, 0);
                            const expiredUrl = err instanceof HttpErrorResponse && err.status === 403;
                            if (expiredUrl) urls.delete(part);
                            if (attempt >= MAX_PART_ATTEMPTS || (!expiredUrl && !this.isTransientUploadError(err))) throw err;
                            await waitForRetry(attempt);
                        }
                    }
                }
            };
            await Promise.all(Array.from({length: Math.min(CONCURRENCY, init.part_count)}, worker));
            check();
            const result = await retryRequest(() => this.http.post<{upload_id: string}>(`${base}/${uploadId}/complete/`, {
                parts: parts.sort((a, b) => a.PartNumber - b.PartNumber),
            }, apiOptions));
            check();
            progress.set(1);
            return result;
        })().catch(async err => {
            const uploadError = stopped ? new DirectUploadError('Upload cancelled') : this.toDirectUploadError(err);
            await abort();
            throw uploadError;
        });
        return {done, progress, abort};
    }

    private isTransientUploadError(err: unknown): boolean {
        return err instanceof TimeoutError || (err instanceof HttpErrorResponse &&
            (err.status === 0 || err.status === 408 || err.status === 429 || err.status >= 500));
    }

    async upload(
        file: File,
        memoryId: number,
        type: string,
        metadata: MediaMetadata = {},
    ): Promise<unknown> {
        return this.run(file, {
            initiateUrl: `${environment.apiUrl}v2/api/upload/direct/initiate/`,
            initiateBody: {
                memory: memoryId,
                type,
                filename: this.getUploadFilename(file, type),
                content_type: this.getUploadContentType(file),
                size: file.size,
                ...(metadata.cropped ? {cropped: true} : {}),
            },
            completeExtra: metadata,
            completeMessage: 'Completed - MediaContent created 🎉',
        });
    }

    async uploadDraftItem(
        file: File,
        draftId: string,
        type: string,
        metadata: DraftItemMetadata = {},
    ): Promise<unknown> {
        return this.run(file, {
            initiateUrl: `${environment.apiUrl}v2/api/drafts/${draftId}/items/direct/initiate/`,
            initiateBody: {
                type,
                filename: this.getUploadFilename(file, type),
                content_type: this.getUploadContentType(file),
                size: file.size,
            },
            completeExtra: metadata,
            completeMessage: 'Completed - draft item created',
        });
    }

    async replaceMediaFile(
        file: File,
        mediaId: number,
        type: string,
        cropped: boolean = false,
    ): Promise<unknown> {
        return this.run(file, {
            initiateUrl: `${environment.apiUrl}v2/api/media/${mediaId}/file/direct/initiate/`,
            initiateBody: {
                type,
                filename: this.getUploadFilename(file, type),
                content_type: this.getUploadContentType(file),
                size: file.size,
                ...(cropped ? {cropped: true} : {}),
            },
            completeExtra: {},
            completeMessage: 'Completed - media file replaced',
        });
    }

    private async run(
        file: File,
        target: DirectUploadTarget,
    ): Promise<unknown> {
        this.progress.set(0);
        this.error.set(null);
        this.log.set([]);

        let init: InitiateResponse | undefined;

        try {
            // 1. Initiate
            init = await firstValueFrom(
                this.http.post<InitiateResponse>(target.initiateUrl, target.initiateBody),
            );
            this.say(
                `Initiated upload ${init.upload_id}: ${init.part_count} part(s) of ` +
                `${(init.part_size / 1024 / 1024).toFixed(0)} MB, ` +
                `${Object.keys(init.part_urls).length} URL(s) presigned up front`,
            );

            const initiated: InitiateResponse = init;
            const urls = new Map<number, string>(
                Object.entries(initiated.part_urls).map(([n, u]) => [Number(n), u]),
            );
            const uploadedBytes = new Array<number>(initiated.part_count).fill(0);
            const completed: CompletedPart[] = [];

            // 2. Upload parts straight to the bucket with a small worker pool
            let nextPart = 1;
            const worker = async () => {
                while (nextPart <= initiated.part_count) {
                    const partNumber = nextPart++;
                    completed.push(
                        await this.uploadPart(initiated, file, partNumber, urls, (loaded) => {
                            uploadedBytes[partNumber - 1] = loaded;
                            this.progress.set(uploadedBytes.reduce((a, b) => a + b, 0) / file.size);
                        }),
                    );
                    this.say(`Part ${partNumber}/${initiated.part_count} uploaded`);
                }
            };
            await Promise.all(
                Array.from({length: Math.min(CONCURRENCY, initiated.part_count)}, worker),
            );

            // 3. Complete - Django stitches the parts and creates the MediaContent
            const media = await firstValueFrom(
                this.http.post(`${environment.apiUrl}v2/api/upload/direct/${initiated.upload_id}/complete/`, {
                    parts: completed.sort((a, b) => a.PartNumber - b.PartNumber),
                    ...target.completeExtra,
                }),
            );
            this.say(target.completeMessage);
            return media;
        } catch (err) {
            const uploadError = this.toDirectUploadError(err);
            this.error.set(uploadError);
            this.say(`Failed (${uploadError.message}) - aborting upload`);
            if (init) {
                await firstValueFrom(
                    this.http.post(`${environment.apiUrl}v2/api/upload/direct/${init.upload_id}/abort/`, {}),
                ).catch(() => undefined);
            }
            throw uploadError;
        }
    }

    private async uploadPart(
        init: InitiateResponse,
        file: File,
        partNumber: number,
        urls: Map<number, string>,
        onProgress: (loadedBytes: number) => void,
    ): Promise<CompletedPart> {
        const start = (partNumber - 1) * init.part_size;
        const blob = file.slice(start, Math.min(start + init.part_size, file.size));

        for (let attempt = 1; ; attempt++) {
            try {
                const url =
                    urls.get(partNumber) ?? (await this.fetchPartUrl(init.upload_id, partNumber));
                const etag = await this.putPart(url, blob, onProgress);
                return {PartNumber: partNumber, ETag: etag};
            } catch (err) {
                urls.delete(partNumber); // force a freshly signed URL on retry
                if (attempt >= MAX_PART_ATTEMPTS) {
                    throw err;
                }
                this.say(`Part ${partNumber} attempt ${attempt} failed (${this.getErrorMessage(err)}), retrying...`);
                await new Promise((r) => setTimeout(r, 1000 * 2 ** (attempt - 1)));
            }
        }
    }

    private putPart(
        url: string,
        blob: Blob,
        onProgress: (loaded: number) => void,
    ): Promise<string> {
        return new Promise((resolve, reject) => {
            this.http
                .put(url, blob, {
                    context: new HttpContext().set(SKIP_AUTH, true),
                    reportProgress: true,
                    observe: 'events',
                })
                .subscribe({
                    next: (event) => {
                        if (event.type === HttpEventType.UploadProgress) {
                            onProgress(event.loaded);
                        } else if (event instanceof HttpResponse) {
                            const etag = event.headers.get('ETag');
                            if (!etag) {
                                reject(
                                    new Error(
                                        'No ETag on part response - is the bucket CORS ExposeHeaders rule applied?',
                                    ),
                                );
                                return;
                            }
                            resolve(etag);
                        }
                    },
                    error: (err: unknown): void => {
                        reject(this.toDirectUploadError(err));
                    },
                });
        });
    }

    private async fetchPartUrl(uploadId: string, partNumber: number): Promise<string> {
        const res = await firstValueFrom(
            this.http.post<{ part_urls: Record<string, string> }>(
                `${environment.apiUrl}v2/api/upload/direct/${uploadId}/part-urls/`,
                {part_numbers: [partNumber]},
            ),
        );
        return res.part_urls[String(partNumber)];
    }

    private say(message: string): void {
        this.log.update((lines) => [...lines, message]);
        console.log(`[direct-upload] ${message}`);
    }

    private getUploadFilename(file: File, type: string): string {
        return `upload.${this.getUploadExtension(file, type)}`;
    }

    private getUploadExtension(file: File, type: string): string {
        const extensionFromName: string | undefined = file.name
            .split('.')
            .pop()
            ?.toLowerCase()
            .replace(/[^a-z0-9]/g, '');

        const extensionFromMime: string | undefined = this.getExtensionFromMimeType(file.type);

        return extensionFromMime || this.getSafeExtension(extensionFromName) || this.getDefaultExtension(type);
    }

    private getExtensionFromMimeType(mimeType: string): string | undefined {
        const mimeMap: Record<string, string> = {
            'image/jpeg': 'jpg',
            'image/png': 'png',
            'image/gif': 'gif',
            'image/webp': 'webp',
            'image/heic': 'heic',
            'image/heif': 'heif',
            'video/mp4': 'mp4',
            'video/quicktime': 'mov',
            'video/webm': 'webm',
            'audio/mpeg': 'mp3',
            'audio/mp3': 'mp3',
            'audio/mp4': 'm4a',
            'audio/x-m4a': 'm4a',
            'audio/wav': 'wav',
            'audio/webm': 'webm',
            'application/pdf': 'pdf',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
        };

        const normalizedMimeType: string = this.normalizeMimeType(mimeType);

        return mimeMap[normalizedMimeType];
    }

    private getUploadContentType(file: File): string {
        return this.normalizeMimeType(file.type) || 'application/octet-stream';
    }

    private normalizeMimeType(mimeType: string | undefined): string {
        return mimeType?.split(';')[0]?.trim().toLowerCase() || '';
    }

    private getDefaultExtension(type: string): string {
        const typeMap: Record<string, string> = {
            image: 'jpg',
            video: 'mp4',
            audio: 'mp3',
            document: 'pdf',
        };

        return typeMap[type] || 'bin';
    }

    private getSafeExtension(extension?: string): string | undefined {
        if (!extension || extension.length > 8) {
            return undefined;
        }

        return extension;
    }

    private toDirectUploadError(err: unknown): DirectUploadError {
        if (err instanceof DirectUploadError) {
            return err;
        }

        return new DirectUploadError(this.getErrorMessage(err), err);
    }

    private getErrorMessage(err: unknown): string {
        if (err instanceof HttpErrorResponse) {
            if (err.error instanceof ErrorEvent) {
                return err.error.message;
            }

            const serverMessage = typeof err.error === 'string'
                ? err.error
                : err.error?.detail || err.error?.message;

            return serverMessage || `${err.status || 'Network'} ${err.statusText || 'upload error'}`;
        }

        if (err instanceof Error) {
            return err.message;
        }

        return String(err);
    }
}
